import dateFormatter from '@/services/formatter_services/date-formatter';
import { formatBool } from '@/utils/shared';
import TRUNCATE_UNITS from '@/utils/enum/TRUNCATE_UNITS';

const EMPLOYEES_WIDGETS = Object.freeze({
  EMPLOYEES_OVERVIEW_WIDGETS: [
    {
      id: 'employeesOverview',
      path: 'employees',
      getData: response => response.data.data?.map(item => item.data),
      headers: [
        {
          text: 'Dashboard.widgets.employeesOverview.headers.personnellNumber',
          value: 'personnellNumber'
        },
        {
          text: 'Dashboard.widgets.employeesOverview.headers.contractStart',
          value: 'contractStart',
          presentWith: dateFormatter.formatDateToLocaleDateStringNoTime
        },
        {
          text: 'Dashboard.widgets.employeesOverview.headers.contractLimitationEnd',
          value: 'contractLimitationEnd',
          presentWith: dateFormatter.formatDateToLocaleDateStringNoTime
        },
        {
          text: 'Dashboard.widgets.employeesOverview.headers.contractEnd',
          value: 'contractEnd',
          presentWith: dateFormatter.formatDateToLocaleDateStringNoTime
        },
        {
          text: 'Dashboard.widgets.employeesOverview.headers.fullname',
          value: 'fullname'
        },
        {
          text: 'Dashboard.widgets.employeesOverview.headers.departments',
          value: 'departments'
        },
        {
          text: 'Dashboard.widgets.employeesOverview.headers.groups',
          value: 'groups'
        },
        {
          text: 'Common.type',
          value: 'employeeType'
        }
      ],
      type: 'TABLE',
      moduleId: 'employeesOverview',
      withRouterActionLink: true
    },
    {
      id: 'employeesAmount',
      path: 'employees',
      type: 'VALUE',
      getData: response => response.data.totalHits,
      moduleId: 'employeesOverview'
    },
  ],
  USERS_OVERVIEW_WIDGETS: [
    {
      id: 'usersOverview',
      path: 'users',
      getData: response => response.data.data?.map(item => item.data),
      headers: [
        {
          text: 'Common.name',
          value: 'fullname',
          truncateUnit: TRUNCATE_UNITS.STRING,
          truncateAfter: 20,
        },
        { text: 'Dashboard.widgets.usersOverview.headers.userName', value: 'userName' },
        { text: 'Dashboard.widgets.usersOverview.headers.active', value: 'isActive', presentWith: formatBool },
        { text: 'Dashboard.widgets.usersOverview.headers.login', value: 'loginType' }
      ],
      type: 'TABLE',
      moduleId: 'userOverview',
      withRouterActionLink: true
    },
    {
      id: 'usersAmount',
      path: 'users',
      type: 'VALUE',
      getData: response => response.data.totalHits,
      moduleId: 'userOverview'
    },
  ],
  ACCESSGROUPS_WIDGETS: [
    {
      id: 'accessgroupsOverview',
      path: 'accessgroups',
      headers: [
        { text: 'Common.label', value: 'label' },
        { text: 'Common.description', value: 'description' },
      ],
      type: 'TABLE',
      moduleId: 'accessgroupsOverview',
      withRouterActionLink: true
    },
  ],
  ROLES_WIDGETS: [
    {
      id: 'rolesOverview',
      path: 'roles',
      getData: response => response.data.data?.map(item => item.data),
      headers: [
        { text: 'Common.label', value: 'label' },
        { text: 'Dashboard.widgets.rolesOverview.headers.isStandardRole', value: 'isStandardRole', presentWith: formatBool },
        { text: 'Dashboard.widgets.rolesOverview.headers.amountUsers', value: 'users' },
        { text: 'Dashboard.widgets.rolesOverview.headers.rights', value: 'rights' }
      ],
      type: 'TABLE',
      moduleId: 'rolesOverview',
      withRouterActionLink: true
    },
    {
      id: 'rolesAmount',
      path: 'roles',
      type: 'VALUE',
      getData: response => response.data.totalHits,
      moduleId: 'roleOverview'
    },
  ],
  OCCUPATION_WIDGETS: [
    {
      id: 'occupationgroups',
      path: 'occupationgroups',
      getData: response => response.data.data?.map(item => item.data),
      headers: [
        {
          text: 'Common.label',
          value: 'label',
          truncateUnit: TRUNCATE_UNITS.STRING,
          truncateAfter: 20,
        },
        {
          text: 'Common.description',
          value: 'description',
          truncateUnit: TRUNCATE_UNITS.STRING,
          truncateAfter: 20,
        },
        { text: 'Dashboard.widgets.occupationgroups.headers.productive', value: 'productive', presentWith: formatBool },
        { text: 'Dashboard.widgets.occupationgroups.headers.connectedEmployees', value: 'connectedEmployees' },
        { text: 'Dashboard.widgets.occupationgroups.headers.manager', value: 'manager' }
      ],
      type: 'TABLE',
      moduleId: 'occupationGroupOverview',
      withRouterActionLink: true
    },
    {
      id: 'occupationgroupsAmount',
      path: 'occupationgroups',
      type: 'VALUE',
      getData: response => response.data.totalHits,
      moduleId: 'occupationGroupOverview'
    }
  ],
  GROUPS_WIDGETS: [
    {
      id: 'groups',
      path: 'groups',
      getData: response => response.data.data?.map(item => item.data),
      headers: [
        {
          text: 'Common.label',
          value: 'label',
          truncateUnit: TRUNCATE_UNITS.STRING,
          truncateAfter: 20,
        },
        { text: 'Dashboard.widgets.groups.headers.department', value: 'department' },
        { text: 'Dashboard.widgets.groups.headers.manager', value: 'manager' }
      ],
      type: 'TABLE',
      moduleId: 'groupOverview',
      withRouterActionLink: true
    },
    {
      id: 'groupsAmount',
      path: 'groups',
      type: 'VALUE',
      getData: response => response.data.totalHits,
      moduleId: 'groupOverview'
    }
  ],
  DEPARTMENT_WIDGETS: [
    {
      id: 'departmentOverview',
      path: 'departments',
      getData: response => response.data.data?.map(item => item.data),
      headers: [
        { text: 'Dashboard.widgets.departmentOverview.headers.departmentNumber', value: 'departmentNumber' },
        { text: 'Common.label', value: 'label' },
        { text: 'Dashboard.widgets.departmentOverview.headers.manager', value: 'manager' },
        { text: 'Dashboard.widgets.departmentOverview.headers.abbreviation', value: 'abbreviation' },
        { text: 'Dashboard.widgets.departmentOverview.headers.state', value: 'state' }
      ],
      type: 'TABLE',
      moduleId: 'departmentOverview',
      withRouterActionLink: true
    },
    {
      id: 'departmentsAmount',
      path: 'departments',
      type: 'VALUE',
      getData: response => response.data.totalHits,
      moduleId: 'departmentOverview'
    }
  ]
});

export default EMPLOYEES_WIDGETS;
